// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * Plane geometry for the corner-finding stage.
 *
 * Everything here is pure and allocation-light so it can run in the worker on a
 * few thousand edge points without becoming the bottleneck.
 */

export interface Point {
  x: number;
  y: number;
}

/** Four corners in clockwise screen order: top-left, top-right, bottom-right, bottom-left. */
export type Quad = [Point, Point, Point, Point];

export function distance(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/** Twice the signed area of triangle `abc`. Positive when `abc` turns anticlockwise. */
export function cross(o: Point, a: Point, b: Point): number {
  return (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
}

export function triangleArea(a: Point, b: Point, c: Point): number {
  return Math.abs(cross(a, b, c)) / 2;
}

/** Shoelace area of a simple polygon, unsigned. */
export function polygonArea(points: readonly Point[]): number {
  let sum = 0;
  for (let i = 0; i < points.length; i++) {
    const a = points[i];
    const b = points[(i + 1) % points.length];
    sum += a.x * b.y - b.x * a.y;
  }
  return Math.abs(sum) / 2;
}

/**
 * Convex hull by Andrew's monotone chain. Returns the hull vertices in a
 * consistent cyclic order with collinear points removed. Fewer than three
 * distinct points have no hull, so the input is passed straight back.
 */
export function convexHull(points: readonly Point[]): Point[] {
  const sorted = points.slice().sort((a, b) => a.x - b.x || a.y - b.y);

  // Duplicate points break the turn test, and edge detection produces plenty.
  const unique: Point[] = [];
  for (const p of sorted) {
    const last = unique[unique.length - 1];
    if (!last || last.x !== p.x || last.y !== p.y) unique.push(p);
  }
  if (unique.length < 3) return unique;

  const build = (seq: readonly Point[]): Point[] => {
    const chain: Point[] = [];
    for (const p of seq) {
      while (chain.length >= 2 && cross(chain[chain.length - 2], chain[chain.length - 1], p) <= 0) {
        chain.pop();
      }
      chain.push(p);
    }
    chain.pop();
    return chain;
  };

  const lower = build(unique);
  const upper = build(unique.slice().reverse());
  return lower.concat(upper);
}

/**
 * Thins a hull down to at most `max` vertices by keeping evenly spaced ones.
 * `maxAreaQuad` is cubic in the vertex count, so a hull with hundreds of
 * near-collinear vertices needs trimming before it gets there.
 */
export function decimateHull(hull: readonly Point[], max: number): Point[] {
  if (hull.length <= max) return hull.slice();
  const step = hull.length / max;
  const out: Point[] = [];
  for (let i = 0; i < max; i++) out.push(hull[Math.floor(i * step)]);
  return out;
}

/**
 * The largest-area quadrilateral whose corners are hull vertices.
 *
 * For each pair of vertices treated as a diagonal, the best third vertex on
 * each side is the one furthest from that diagonal — so the search is over
 * diagonals, with a linear scan per side.
 */
export function maxAreaQuad(hull: readonly Point[]): Quad | null {
  const n = hull.length;
  if (n < 4) return null;
  if (n === 4) return orderCorners(hull);

  let bestArea = 0;
  let best: [number, number, number, number] | null = null;

  for (let i = 0; i < n; i++) {
    for (let j = i + 2; j < n; j++) {
      let kArea = -1;
      let kIndex = -1;
      for (let k = i + 1; k < j; k++) {
        const area = triangleArea(hull[i], hull[k], hull[j]);
        if (area > kArea) {
          kArea = area;
          kIndex = k;
        }
      }

      let lArea = -1;
      let lIndex = -1;
      for (let l = j + 1; l < n + i; l++) {
        const idx = l % n;
        const area = triangleArea(hull[j], hull[idx], hull[i]);
        if (area > lArea) {
          lArea = area;
          lIndex = idx;
        }
      }

      if (kIndex < 0 || lIndex < 0) continue;
      if (kArea + lArea > bestArea) {
        bestArea = kArea + lArea;
        best = [i, kIndex, j, lIndex];
      }
    }
  }

  if (!best) return null;
  return orderCorners(best.map((idx) => hull[idx]));
}

/**
 * Normalises any four points into clockwise screen order starting top-left.
 *
 * Sorting by angle around the centroid recovers the cyclic order. Choosing
 * where that cycle *starts* decides which way up the flattened page comes out,
 * so it can't be done by picking the corner nearest the origin — for a page
 * lying at an angle on the desk, the corner with the smallest x+y is often not
 * the top-left one, and the scan lands sideways.
 *
 * Instead: in a clockwise cycle the top edge is the one that runs left-to-right,
 * so the rotation whose leading edge points most nearly along +x is the one
 * that starts at the top-left corner.
 */
export function orderCorners(points: readonly Point[]): Quad {
  if (points.length !== 4) throw new Error(`orderCorners needs 4 points, got ${points.length}`);

  const cx = (points[0].x + points[1].x + points[2].x + points[3].x) / 4;
  const cy = (points[0].y + points[1].y + points[2].y + points[3].y) / 4;

  // With y pointing down, increasing atan2 walks the corners clockwise.
  const byAngle = points
    .slice()
    .sort((a, b) => Math.atan2(a.y - cy, a.x - cx) - Math.atan2(b.y - cy, b.x - cx));

  let start = 0;
  let bestScore = Infinity;
  for (let r = 0; r < 4; r++) {
    const from = byAngle[r];
    const to = byAngle[(r + 1) % 4];
    // 0 for an edge pointing due right, π for one pointing due left.
    const score = Math.abs(Math.atan2(to.y - from.y, to.x - from.x));
    if (score < bestScore) {
      bestScore = score;
      start = r;
    }
  }

  return [
    byAngle[start],
    byAngle[(start + 1) % 4],
    byAngle[(start + 2) % 4],
    byAngle[(start + 3) % 4],
  ];
}

/** True when the quad has no reflex corner — i.e. it isn't self-crossing or folded. */
export function isConvex(quad: Quad): boolean {
  let sign = 0;
  for (let i = 0; i < 4; i++) {
    const c = cross(quad[i], quad[(i + 1) % 4], quad[(i + 2) % 4]);
    if (c === 0) continue;
    const s = Math.sign(c);
    if (sign === 0) sign = s;
    else if (s !== sign) return false;
  }
  return sign !== 0;
}

/**
 * Output dimensions for a flattened quad.
 *
 * Recovering a document's true aspect ratio from one photo requires knowing the
 * camera's focal length, which we don't have. Taking the longer of each pair of
 * opposite edges is a good approximation for the shallow angles people actually
 * shoot at, and any error is a slight stretch rather than a wrong crop.
 */
export function quadOutputSize(quad: Quad, maxDimension = 2200): { width: number; height: number } {
  const width = Math.max(distance(quad[0], quad[1]), distance(quad[3], quad[2]));
  const height = Math.max(distance(quad[0], quad[3]), distance(quad[1], quad[2]));

  const w = Math.max(1, Math.round(width));
  const h = Math.max(1, Math.round(height));

  const scale = Math.min(1, maxDimension / Math.max(w, h));
  return { width: Math.max(1, Math.round(w * scale)), height: Math.max(1, Math.round(h * scale)) };
}

/** An inset rectangle, used as the starting quad when detection finds nothing. */
export function insetQuad(width: number, height: number, inset = 0.08): Quad {
  const dx = width * inset;
  const dy = height * inset;
  return [
    { x: dx, y: dy },
    { x: width - dx, y: dy },
    { x: width - dx, y: height - dy },
    { x: dx, y: height - dy },
  ];
}

/** Keeps a quad's corners inside the image after the user has dragged them. */
export function clampQuad(quad: Quad, width: number, height: number): Quad {
  return quad.map((p) => ({
    x: Math.min(Math.max(p.x, 0), width),
    y: Math.min(Math.max(p.y, 0), height),
  })) as Quad;
}

export function scaleQuad(quad: Quad, factor: number): Quad {
  return quad.map((p) => ({ x: p.x * factor, y: p.y * factor })) as Quad;
}

/**
 * Pulls every corner a little towards the quad's centre.
 *
 * Detection blurs before measuring edge strength, so the strongest gradient sits
 * a pixel or two *outside* the paper and the corners land slightly wide. Left
 * alone that puts a thin dark rim of desk around every scan. A fraction of a
 * percent is enough to land inside the sheet, and is far smaller than any
 * document's own margin.
 */
export function shrinkQuad(quad: Quad, fraction: number): Quad {
  const cx = (quad[0].x + quad[1].x + quad[2].x + quad[3].x) / 4;
  const cy = (quad[0].y + quad[1].y + quad[2].y + quad[3].y) / 4;
  return quad.map((p) => ({
    x: p.x + (cx - p.x) * fraction,
    y: p.y + (cy - p.y) * fraction,
  })) as Quad;
}
